import { SelfContainedCommand, CommandExecutionContext } from "../../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../../types/standardToolResults";
import { createFileLogger } from "../../../utils/logger";
import { entityService } from "../../../services/entityService";

const log = createFileLogger('APP_BUILDER_UPDATE');

export const appBuilderUpdateCommand: SelfContainedCommand = {
  id: 'app_builder_update',
  description: `Update metadata for an app_project entity — its name, description, and/or icon.

Use this whenever you want to give the app a meaningful name, a short description, or a
distinctive icon. Call it right after creating the app or any time the purpose of the app
becomes clear.

**ICON VALUES (choose the one that best matches the app):**
- Phosphor icon name in PascalCase (recommended):
  "Rocket", "Globe", "Database", "ChartBar", "Calculator", "Code",
  "ShoppingCart", "Calendar", "User", "MusicNote", "Camera", "Heart"
- Emoji string: "🚀", "📊", "🎵", "🛒", "📅"

**EXAMPLES:**

{ "entityId": "app_proj_xyz", "name": "Sales Dashboard", "icon": "ChartBar", "description": "Visualises monthly revenue from bank transactions" }
{ "entityId": "app_proj_xyz", "icon": "🚀" }
{ "entityId": "app_proj_xyz", "name": "My Todo App" }

**RETURNS:** Updated entity details
`,
  autonomyLevel: 'auto',
  visualTreatment: 'narrative',

  schema: {
    parameters: [
      {
        name: 'entityId',
        type: 'string',
        description: 'app_project or website_project entity ID to update',
        required: true
      },
      {
        name: 'name',
        type: 'string',
        description: 'New display name for the app',
        required: false
      },
      {
        name: 'description',
        type: 'string',
        description: 'Short description of what the app does (stored in skeleton.description)',
        required: false
      },
      {
        name: 'icon',
        type: 'string',
        description: 'Icon for the app — Phosphor icon name in PascalCase (e.g. "Rocket", "ChartBar") or emoji (e.g. "🚀")',
        required: false
      }
    ]
  },

  examples: { good: [], bad: [] },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { entityId, name, description, icon } = params;

    if (!entityId) {
      return createErrorResult('app_builder_update requires entityId', 'entityId is required', 'validation');
    }

    if (!name && !description && icon === undefined) {
      return createErrorResult('app_builder_update: nothing to update', 'Provide at least one of: name, description, icon', 'validation');
    }

    try {
      log.info('[app_builder_update] Updating app entity:', { entityId, hasName: !!name, hasIcon: icon !== undefined, hasDescription: !!description });

      const entityUpdates: Record<string, any> = {};

      if (name) {
        entityUpdates.name = name;
      }

      if (icon !== undefined) {
        // entity.icon is always stored as a string (Phosphor name or emoji).
        // If the AI passes a full AppIcon object, extract the meaningful string from it
        // so that iconService.parseIcon (which only handles strings) doesn't crash.
        if (typeof icon === 'object' && icon !== null) {
          const iconObj = icon as any;
          entityUpdates.icon = iconObj.phosphorIcon || iconObj.emoji || 'Sparkle';
        } else {
          entityUpdates.icon = icon;
        }
      }

      if (description) {
        // The server merges skeleton patches, so we can pass only description.
        entityUpdates.skeleton = { description };
      }

      const updated = await entityService.update(entityId, entityUpdates);

      log.info('[app_builder_update] ✅ Updated:', entityId);

      // Immediately push the updated entity into the store so that the sidebar,
      // window tabs, and AppBuilderApp toolbar all re-render without waiting for
      // the WebSocket/CRDT round-trip. Isolated from the main try-catch so a
      // store failure never masks a successful backend update.
      try {
        const { default: useEntityStore } = await import('../../../stores/entityStore');
        useEntityStore.getState()._applyLocalUpsert(updated as any, { source: 'local' });
      } catch (storeError: any) {
        log.error('[app_builder_update] Store sync failed (update still persisted):', storeError);
      }

      return createSuccessResult(
        `App updated successfully.${name ? ` Name: "${name}".` : ''}${icon !== undefined ? ` Icon set.` : ''}${description ? ` Description updated.` : ''}`,
        {
          entityId,
          name: (updated as any)?.name,
          icon: (updated as any)?.icon,
        }
      );
    } catch (error: any) {
      log.error('[app_builder_update] Failed:', error);
      return createErrorResult('Failed to update app', error.message || 'Unknown error', 'execution');
    }
  }
};
